'use client';

import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import CloseIcon from '@/fo/icons/base/icons/close10.svg';
import SoundIcon from '@/fo/icons/base/icons/sound.svg';
import { Doctor } from '@/fo/ui/doctor/profile/page';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDialogBase } from '@/fo/ui/kit/dialog/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { getMediaBaseUrl } from '@/fo/util/image-loader';

import { styles } from './FeatureProductExpertVideoBase.styles';

export interface FeatureProductExpertVideoBaseProps {
  videoExpertOpinionPreview?: string;
  videoExpertOpinionVideo?: string;
  videoExpertOpinionExpertUrlKey?: string;
  productName?: string;
  doctor?: Doctor | null;
}

export function FeatureProductExpertVideoBase({
  videoExpertOpinionPreview,
  videoExpertOpinionVideo,
  productName: initialProductName,
  doctor
}: FeatureProductExpertVideoBaseProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [productName, setProductName] = useState(initialProductName);
  const slots = styles();
  const t = useTranslations();
  const isMobile = useMediaQuery('lg', 'max');

  useEffect(() => {
    return utilEventConfigurableProduct.onProductSelect((newProduct) =>
      setProductName(newProduct.name)
    );
  }, []);

  const hasValidPreview =
    videoExpertOpinionPreview &&
    !videoExpertOpinionPreview.includes('no_selection');

  if (!isVisible) {
    return null;
  }

  return (
    <div className={slots.base()}>
      <div
        className={slots.button()}
        onClick={() => setIsModalOpen(true)}
      >
        <UiKitButtonBase
          as="button"
          className={slots.close()}
          onClick={(event) => {
            event.stopPropagation();
            setIsVisible(false);
          }}
        >
          <CloseIcon />
        </UiKitButtonBase>
        {hasValidPreview ? (
          <Image
            src={
              getMediaBaseUrl() +
              '/catalog/product/' +
              videoExpertOpinionPreview
            }
            alt={t('product.expert.title')}
            width={150}
            height={150}
            className={slots.preview()}
          />
        ) : (
          videoExpertOpinionVideo && (
            <video
              src={videoExpertOpinionVideo}
              className={slots.preview()}
              width={150}
              height={150}
              muted
              playsInline
              title={`${t('product.expert.title')} - ${doctor?.full_name}`}
            />
          )
        )}
        <p className={slots.title()}>{t('product.expert.title')}</p>
      </div>

      <UiKitDialogBase
        title={t('product.expert.title')}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        className={slots.modal()}
        fullScreen={isMobile}
      >
        <div className={slots.leftWrapper()}>
          <div className={slots.topMobile()}>
            <p className={slots.heading()}>{t('product.expert.title')}</p>
            {productName && (
              <p className={slots.productTitle()}>{productName}</p>
            )}
          </div>
          <div className={slots.video()}>
            <div className={slots.videoWrapper()}>
              <video
                className={slots.videoExpert()}
                title={`${t('product.expert.title')} - ${doctor?.full_name}`}
                src={videoExpertOpinionVideo}
                autoPlay
                loop
                muted={isMuted}
                playsInline
              />
            </div>
            <button
              type="button"
              className={slots.soundButton({
                muted: isMuted
              })}
              onClick={() => setIsMuted((prev) => !prev)}
            >
              <SoundIcon />
              <span className={slots.soundButtonText()}>
                {isMuted
                  ? t('product.expert.sound.on')
                  : t('product.expert.sound.off')}
              </span>
            </button>
          </div>
        </div>
        <div className={slots.content()}>
          <div className={slots.topDesktop()}>
            <p className={slots.heading()}>{t('product.expert.title')}</p>
            {productName && (
              <p className={slots.productTitle()}>{productName}</p>
            )}
          </div>

          <div className={slots.bottom()}>
            {doctor?.photo && (
              <div className={slots.avatarWrap()}>
                <Image
                  src={getMediaBaseUrl() + doctor.photo}
                  alt={doctor.photo_alt || doctor.full_name}
                  width={80}
                  height={80}
                  className={slots.avatar()}
                />
              </div>
            )}
            <div className={slots.info()}>
              {doctor?.full_name && (
                <p className={slots.name()}>{doctor.full_name}</p>
              )}
              {doctor?.occupation && doctor.occupation.length > 0 && (
                <p className={slots.job()}>{doctor.occupation.join(', ')}</p>
              )}
            </div>
          </div>
        </div>
      </UiKitDialogBase>
    </div>
  );
}
